import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { post } from "@/lib/api";
import { centsEur } from "@/lib/documents";
import type { Document, ReadyRow } from "@/lib/types";

const PREVIEW = 4;

export function ReadyList({ rows }: { rows: ReadyRow[] | undefined }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const draft = useMutation({
    mutationFn: (clientId: number) => post<{ document: Document; warning: string | null }>("/api/documents/drafts", { clientId }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      if (r.warning) toast.warning(r.warning);
      navigate(`/rechnungen/${r.document.id}`);
    },
    onError: (e) => toast.error(e.message),
  });
  const shown = rows && !expanded ? rows.slice(0, PREVIEW) : rows;

  return (
    <section aria-labelledby="h-ready" className="space-y-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2.5">
        <h2 id="h-ready" className="text-base font-semibold">Bereit zum Verrechnen</h2>
        <span className="text-[0.8125rem] text-muted-foreground">aus Buchungen und weiterverrechneten Ausgaben · Stundensatz je Kunde</span>
      </div>
      <div className="overflow-hidden rounded-lg border bg-card">
        {!rows && <div className="h-11 animate-pulse bg-muted motion-reduce:animate-none" aria-hidden />}
        {rows?.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">Nichts zu verrechnen.</p>}
        {shown?.map((r) => (
          <div key={r.clientId} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 border-t px-4 py-2.5 transition-colors duration-100 first:border-t-0 hover:bg-muted motion-reduce:transition-none sm:grid-cols-[minmax(0,1fr)_auto_auto]">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-medium" title={r.clientName}>{r.clientName}</span>
              <span className="flex min-w-0 items-center gap-x-1.5 text-[0.8125rem] text-muted-foreground">
                <span className="truncate" title={r.label}>{r.label}</span>
                {r.stale && (
                  <span className="inline-flex shrink-0 items-center gap-1.5 text-foreground">
                    <span aria-hidden className="size-1.5 rounded-full bg-warning" />{r.stale}
                  </span>
                )}
              </span>
            </div>
            <span className="text-sm font-medium tabular-nums">{centsEur(r.cents)}</span>
            <Button
              variant="outline"
              size="sm"
              className="max-sm:col-span-full max-sm:justify-self-start pointer-coarse:h-11"
              disabled={draft.isPending}
              onClick={() => draft.mutate(r.clientId)}
            >
              Entwurf erstellen
            </Button>
          </div>
        ))}
      </div>
      {rows && rows.length > PREVIEW && !expanded && (
        <Button variant="ghost" size="sm" className="text-muted-foreground pointer-coarse:h-11" onClick={() => setExpanded(true)}>
          Alle {rows.length} Kunden anzeigen
        </Button>
      )}
    </section>
  );
}
